import React, { useEffect, useMemo, useState } from 'react';

import { type FieldProps, Field } from 'formik';
import debounce from 'lodash/debounce';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';

import Select, { type OptionType } from '@fd/ui/Select/Select';

import { useInfiniteQueryStoreFilterHook } from '../../../custom-hooks/useInfiniteQuerySoreFilter';
import { loadByAppId } from '../../../services/store.service';
import { getSalesChannelType } from '../../../ui/utils/generateSalesChannelsTypes';
import FieldWrapper from '../../Finance/Banking/components/fields/FieldWrapper';
import useStoreToSalesChannelTransformerWithQuery from '../../Settings/utils/useStoreToSalesChannelTransformerWithQuery';

type ExtendedOptionType = OptionType & {
  brandName?: string;
  salesChannelName?: string;
  salesChannelType?: string;
  value: OptionType['value'] | 'all';
};

type OuterProps = {
  fieldName: string;
  preSelectedStores?: ExtendedOptionType[];
};

type InnerProps = MappedState;
type Props = InnerProps & OuterProps;

const AssignStoresField: React.FC<React.PropsWithChildren<Props>> = (props: Props) => {
  const { appId, preSelectedStores, translate } = props;
  const [query, setQuery] = useState('');
  const [selectedStores, setSelectedStores] = useState<ExtendedOptionType[]>([]);
  const [storeOptions, setStoreOptions] = useState<ExtendedOptionType[]>([]);

  const fetchDataProps = {
    appId,
    query,
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } =
    useInfiniteQueryStoreFilterHook(fetchDataProps, loadByAppId, 'loadByAppId');

  const storeIdAssociations = useStoreToSalesChannelTransformerWithQuery();

  const getAllFilteredStoreOptions = (): ExtendedOptionType[] => {
    if (!data?.pages) {
      return [];
    }

    return data.pages.reduce<ExtendedOptionType[]>((total, page) => {
      if (page.Data) {
        page.Data.forEach((store) => {
          const salesChannelInfo = storeIdAssociations.find(
            (association) => association.storeId === store.StoreId
          );

          total.push({
            label: `${store.Name as string}${getSalesChannelType(salesChannelInfo?.salesChannelType ?? '')}`,
            value: store.StoreId,
            isDisabled: false,
            salesChannelType: salesChannelInfo?.salesChannelType,
            salesChannelName: salesChannelInfo?.salesChannelName,
            brandName: salesChannelInfo?.brandName,
          });
        });
      }
      return total;
    }, []);
  };

  useEffect(() => {
    if (preSelectedStores?.length) {
      setSelectedStores(preSelectedStores);
    }
  }, [preSelectedStores]);

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (data && data.pages) {
      const addNewStoreOptions = getAllFilteredStoreOptions();

      const showAllStoresOption =
        addNewStoreOptions.length > 0 && selectedStores?.length !== addNewStoreOptions.length;
      if (showAllStoresOption) {
        const allStoresOption = {
          label: translate('All_sales_channels') as string,
          value: 'all' as const,
          isDisabled: false,
        };
        setStoreOptions([allStoresOption, ...addNewStoreOptions]);
      } else {
        setStoreOptions(addNewStoreOptions);
      }
    }
  }, [data?.pages, selectedStores]);

  const onSearchQueryChange = useMemo(
    () =>
      debounce((query: string) => {
        setQuery(query);
      }, 500),
    []
  );

  const validate = (value: string[]) => {
    if (value.length === 0 || value === undefined) {
      return <Translate id="Please_select_a_store" />;
    }
    return;
  };

  return (
    <FieldWrapper>
      <Field name={'StoreIds'} validate={validate}>
        {({ field, form }: FieldProps) => {
          const { errors, touched, isSubmitting } = form;
          const fieldError = errors[field.name] as string | undefined;
          const showError = !!fieldError && (touched[field.name] as boolean | undefined);
          const isLoading = isPending || isFetchingNextPage;
          const isDisabled = isSubmitting || isLoading;

          const handleStoresChange = (values: ExtendedOptionType[]) => {
            const allStoresOption = values.find((option) => option.value === 'all');
            if (allStoresOption) {
              const storeOptionsWithoutAll = storeOptions.filter(
                (option) => option.value !== 'all'
              );
              const allStoreIds = storeOptionsWithoutAll.map((option) => option.value);
              setSelectedStores(storeOptionsWithoutAll);
              form.setFieldValue('StoreIds', allStoreIds, false);
              form.errors.StoreIds = undefined;
              return;
            }

            const storeIds = values.map((option) => option.value);
            setSelectedStores(values);
            form.setFieldValue('StoreIds', storeIds, false);
            form.errors.StoreIds = undefined;
          };

          return (
            <Select
              {...field}
              dataFd="campaign-loyalty-store-select"
              fieldError={showError ? fieldError : undefined}
              hideSelectedOptions
              isClearable
              isDisabled={isDisabled}
              isLoading={isLoading}
              isMulti
              onFocus={() => {
                setQuery('');
              }}
              onChange={handleStoresChange}
              options={storeOptions}
              placeholder={''}
              setSearchQuery={onSearchQueryChange}
              TextFieldProps={{
                fdKey: 'stores-dropdown',
                name: 'selected-stores',
                InputLabelProps: { shrink: false },
              }}
              value={selectedStores}
              variant="standard"
            />
          );
        }}
      </Field>
    </FieldWrapper>
  );
};

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => {
  const { locale, currentApp } = state;
  return {
    translate: getTranslate(locale),
    appId: currentApp.AppId!,
  };
};

const EnhancedComponent = connect(mapStateToProps)(AssignStoresField);

export default EnhancedComponent;
